import { useQuery } from '@tanstack/react-query';
import lineageApiService from '../api/lineageApiService';

/**
 * Fetches the organization's lineage discovery status.
 *
 * Why this hook (instead of useEffect + lineageApiService directly): the
 * Dashboard's previous raw useEffect bypassed React Query, so every mount
 * (including StrictMode double-mount and remounts triggered by parent
 * context settling) issued a fresh /lineage/status request. With React
 * Query, all consumers sharing the `['lineage-status']` key dedupe to a
 * single in-flight request, the result is cached for 5 minutes (lineage
 * doesn't change often), and remounts are free.
 */
const STALE_TIME_MS = 5 * 60 * 1000; // lineage status changes rarely

export const useLineageStatus = (options = {}) => {
  return useQuery({
    queryKey: ['lineage-status'],
    queryFn: async () => {
      const response = await lineageApiService.getLineageStatus();
      // The service returns { success, data }. Surface just the data on
      // success. On `success: false` we return null (preserving the
      // pre-migration behavior — the original raw useEffect also did
      // nothing in that case). Real network errors throw and React
      // Query exposes them via `isError` for consumers that care.
      if (response?.success) return response.data;
      return null;
    },
    staleTime: STALE_TIME_MS,
    ...options,
  });
};
